import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { UseMutateFunction, useQueryClient } from "@tanstack/react-query";

import { AltiusErrors, AltiusRes, CreateLinkTokenResponse, PlaidConnection } from "store/types";
import { ApiContext } from "components/providers/Api-provider/ApiContext";
import { useModals } from "../shared-hooks/use-modals";

export const usePlaid = (syncMutation: UseMutateFunction<void, AltiusErrors, string>) => {
  const { altiusApi } = useContext(ApiContext);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  // const [linkTokenError, setLinkTokenError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { errorModal } = useModals();
  const connectionData = useRef<{ investor?: number; id?: number }>({});

  useEffect(() => {
    const init = async () => {
      try {
        const res = await altiusApi.post<AltiusRes<CreateLinkTokenResponse>>({
          url: "/api/v0.0.3/plaid/create-link-token"
        });
        setLinkToken(res.data.data.link_token);
      } catch (_err: any) {
        // setLinkTokenError(err);
        setLinkToken(null);
      }
    };

    init();
  }, []);

  const onSuccess = useCallback(async (publicToken: string) => {
    try {
      if (connectionData.current.id) {
        await altiusApi.patch<AltiusRes<void>>({
          url: `/api/v0.0.3/plaid/connections/${connectionData.current.id}`,
          data: { public_token: publicToken }
        });
        await queryClient.invalidateQueries(["plaid-connections"]);
      } else {
        const connection = await altiusApi.post<AltiusRes<PlaidConnection>>({
          url: "/api/v0.0.3/plaid/connections",
          data: { public_token: publicToken, investor_id: connectionData.current.investor }
        });
        await queryClient.invalidateQueries(["plaid-connections"]);
        await syncMutation(connection.data.data.item_id);
      }
    } catch (err: any) {
      errorModal({ msg: err?.message });
    }
  }, []);

  const config: Parameters<typeof usePlaidLink>[0] = {
    token: linkToken!,
    onSuccess: onSuccess
  };

  const { open, ready } = usePlaidLink(config);

  return {
    open: (connectionId?: number, investorId?: number) => {
      connectionData.current.id = connectionId;
      connectionData.current.investor = investorId;
      open();
    },
    ready
  };
};
